import React, { useState, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { CustomAutoCompleteWithLogo } from "components/CustomComponents/CustomAutoCompleteWithLogo";
import {
  Box,
  Button,
  Card,
  CardHeader,
  CardContent,
  TextField,
  Typography,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Grid,
  Paper,
  FormHelperText,
  Tooltip,
} from "@mui/material";
import {
  ArrowBack,
  Person,
  Email,
  Phone,
  Business,
  LocationOn,
  Language,
  Note,
  Add,
  Delete,
  LinkedIn,
  Twitter,
  Facebook,
  Public,
  Check,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import { customAPI, customGetAPI } from "services/utils/CustomAPI";
import { useSelector } from "react-redux";
import { useFetch } from "services/hooks/useFetch";

// Validation schema (same as ContactFormPage)
const schema = yup
  .object()
  .shape({
    first_name: yup.string().required("First name is required"),
    last_name: yup.string(),
    emails: yup.array().of(
      yup.object().shape({
        email: yup.string().email("Invalid email"),
        type: yup.string().required("Email type is required"),
      })
    ),
    phones: yup.array().of(
      yup.object().shape({
        number: yup.string().required("Phone number is required"),
        type: yup.string().required("Phone type is required"),
        name: yup.string(),
      })
    ),
    company: yup.string(),
    position: yup.string(),
    source: yup.string().required("Source is required"),
    status: yup.string().required("Status is required"),
    notes: yup.string(),
    addresses: yup.array().of(
      yup.object().shape({
        street: yup.string(),
        city: yup.string(),
        state: yup.string(),
        zip: yup.string(),
        country: yup.string(),
        latitude: yup.string().nullable(),
        longitude: yup.string().nullable(),
        type: yup.string().required("Address type is required"),
      })
    ),
    social_media: yup.object().shape({
      linkedin: yup.string().url("Must be a valid URL"),
      twitter: yup.string(),
      facebook: yup.string().url("Must be a valid URL"),
      website: yup.string().url("Must be a valid URL"),
    }),
    assigned_to: yup.string().required("Assigned to is required"),
  })
  .test(
    "contact-info-required",
    "At least one valid phone number is required",
    function (value) {
      const { phones } = value;
      return phones?.some((phone) => phone.number?.trim());
    }
  );

const emailTypeOptions = [
  { value: "work", label: "Work" },
  { value: "personal", label: "Personal" },
  { value: "other", label: "Other" },
];

const phoneTypeOptions = [
  { value: "mobile", label: "Mobile" },
  { value: "work", label: "Work" },
  { value: "home", label: "Home" },
  { value: "other", label: "Other" },
];

const addressTypeOptions = [
  { value: "work", label: "Work" },
  { value: "home", label: "Home" },
  { value: "shipping", label: "Shipping" },
  { value: "billing", label: "Billing" },
];

// Reusable Section component (same as ContactFormPage)
const Section = ({ title, icon, children, ...props }) => (
  <Card
    sx={{
      mb: 3,
      borderLeft: "4px solid",
      borderColor: "primary.main",
      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
      ...props.sx,
    }}
  >
    <CardHeader
      title={
        <Box display="flex" alignItems="center" gap={1}>
          {React.createElement(icon, { color: "primary" })}
          <Typography variant="h6" component="div" fontWeight="600">
            {title}
          </Typography>
        </Box>
      }
      sx={{
        pb: 0,
        bgcolor: "background.paper",
        borderBottom: "1px solid",
        borderColor: "divider",
      }}
    />
    <CardContent sx={{ pt: 2 }}>{children}</CardContent>
  </Card>
);

export default function EditContactPage() {
  const { id } = useParams();
  const history = useHistory();
  const { domain } = useSelector((state) => state.tokenReducer);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [empData, setEmpData] = useState(null);
  const [showAutoCompleteAvatar, setShowAutoCompleteAvatar] = useState(false);
  const { result: users } = useFetch(
    `${domain}crm/product-based/user/?product=CRM`
  );
  const [statusOptions, setStatusOptions] = useState([]);
  const [sourceOptions, setSourceOptions] = useState([]);

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    watch,
    setError,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const formValues = watch();
  useEffect(() => {
    const fetchStatusOptions = async () => {
      try {
        const res = await customGetAPI(`${domain}crm/contact-status/`);
        console.log(res);
        setStatusOptions(res.responseDetails || []);
      } catch (err) {
        console.error("Failed to fetch contact status:", err);
      }
    };

    const fetchSourceOptions = async () => {
      try {
        const res = await customGetAPI(`${domain}crm/contact-source/`);
        setSourceOptions(res.responseDetails || []);
      } catch (err) {
        console.error("Failed to fetch contact sources:", err);
      }
    };

    fetchStatusOptions();
    fetchSourceOptions();
  }, [domain]);

  useEffect(() => {
    const loadContact = async () => {
      try {
        const response = await customGetAPI(`${domain}crm/contacts/${id}/`);
        const contact = response.responseDetails;
        const assignedUser = users.find(
          (user) => user.id === contact.assigned_to
        );
        setEmpData(assignedUser || null); // Store raw user object

        const formData = {
          first_name: contact.first_name,
          last_name: contact.last_name,
          company: contact.company,
          position: contact.position,
          assigned_to: contact.assigned_to,

          source: contact.source,
          status: contact.status,
          notes: contact.notes,
          social_media: {
            linkedin: contact.linkedin || "",
            twitter: contact.twitter || "",
            facebook: contact.facebook || "",
            website: contact.website || "",
          },
          emails: contact.emails || [
            { email: "", type: "work", primary: true },
          ],
          phones: contact.phone_numbers?.map((phone) => ({
            number: phone.number,
            type: phone.type,
            name: phone.reference,
            primary: true,
          })) || [{ number: "", type: "mobile", primary: true, name: "" }],
          addresses: contact.addresses || [
            {
              street: "",
              city: "",
              state: "",
              zip: "",
              country: "",
              latitude: "",
              longitude: "",
              type: "work",
              primary: true,
            },
          ],
        };

        reset(formData);
        if (users?.length) {
          const assignedUser = users.find(
            (user) => user.id === contact.assigned_to
          );
          if (assignedUser) {
            setEmpData({
              ...assignedUser,
              label: assignedUser.username,
              image: assignedUser.image_url,
            });
          }
        }
        setIsLoading(false);
      } catch (error) {
        console.error("Failed to load contact", error);
        toast.error("Failed to load contact");
        history.push("/admin/crm");
      }
    };

    loadContact();
  }, [id, domain, reset, history, users]);

  // Helper functions (same as ContactFormPage)
  const addArrayItem = (arrayName, newItem) => {
    const currentArray = getValues(arrayName);
    setValue(arrayName, [...currentArray, newItem]);
  };

  const removeArrayItem = (arrayName, index) => {
    const currentArray = getValues(arrayName);
    setValue(
      arrayName,
      currentArray.filter((_, i) => i !== index)
    );
  };

  const validateFormData = (data) => {
    let isValid = true;

    // Check for duplicate emails
    if (data.emails) {
      const emailErrors = {};
      const emailValues = data.emails.map((e) => e.email?.trim().toLowerCase());

      data.emails.forEach((email, index) => {
        if (
          email.email &&
          emailValues.indexOf(email.email.trim().toLowerCase()) !== index
        ) {
          emailErrors[index] = { email: { message: "Duplicate email" } };
          isValid = false;
        }
      });

      if (Object.keys(emailErrors).length > 0) {
        Object.entries(emailErrors).forEach(([index, error]) => {
          setError(`emails.${index}`, error);
        });
      }
    }

    // Check for duplicate phone numbers
    if (data.phones) {
      const phoneErrors = {};
      const phoneValues = data.phones.map((p) => p.number?.trim());

      data.phones.forEach((phone, index) => {
        if (
          phone.number &&
          phoneValues.indexOf(phone.number.trim()) !== index
        ) {
          phoneErrors[index] = {
            number: { message: "Duplicate phone number" },
          };
          isValid = false;
        }
      });

      if (Object.keys(phoneErrors).length > 0) {
        Object.entries(phoneErrors).forEach(([index, error]) => {
          setError(`phones.${index}`, error);
        });
      }
    }

    return isValid;
  };

  const preparePayload = (data) => {
    return {
      first_name: data.first_name,
      last_name: data.last_name,
      company: data.company,
      position: data.position,
      assigned_to: data.assigned_to,
      facebook: data.social_media.facebook,
      twitter: data.social_media.twitter,
      linkedin: data.social_media.linkedin,
      phone_numbers: data.phones.map((phone) => ({
        number: phone.number,
        type: phone.type,
        reference: phone.name,
      })),
      emails: data.emails.map((email) => ({
        email: email.email,
        type: email.type,
      })),
      addresses: data.addresses.map((address) => ({
        street: address.street,
        city: address.city,
        state: address.state,
        country: address.country,
        postal_code: address.zip,
        type: address.type,
        latitude: address.latitude,
        longitude: address.longitude,
      })),
      source: data.source,
      status: data.status,
      notes: data.notes,
      website: data.social_media.website,
    };
  };

  useEffect(() => {
    console.log("Form errors:", errors);
  }, [errors]);

  const onSubmit = async (data) => {
    if (!validateFormData(data)) {
      return;
    }

    setIsSaving(true);

    const payload = preparePayload(data);

    try {
      await customAPI(`${domain}crm/contacts/${id}/`, "PUT", payload);
      toast.success("Contact updated successfully!");
      history.push("/admin/crm/contacts");
    } catch (error) {
      console.error("Error updating contact:", error);
      toast.error(error.response?.data?.message || "Failed to update contact");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="80vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", p: 2 }}>
      <Box display="flex" alignItems="center" mb={2}>
        <IconButton
          onClick={() => history.push("/admin/crm/contacts")}
          sx={{ mr: 1 }}
        >
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" fontWeight="bold">
          Edit Contact
        </Typography>
      </Box>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Section title="Contact Information" icon={Person}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Controller
                name="first_name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="First Name *"
                    fullWidth
                    size="small"
                    variant="outlined"
                    error={!!errors.first_name}
                    helperText={errors.first_name?.message}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Person color="primary" />
                        </InputAdornment>
                      ),
                      sx: { borderRadius: 2 },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="last_name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Last Name"
                    fullWidth
                    size="small"
                    variant="outlined"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Person color="primary" />
                        </InputAdornment>
                      ),
                      sx: { borderRadius: 2 },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="company"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Company"
                    fullWidth
                    size="small"
                    variant="outlined"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Business color="primary" />
                        </InputAdornment>
                      ),
                      sx: { borderRadius: 2 },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="position"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Position"
                    fullWidth
                    variant="outlined"
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Business color="primary" />
                        </InputAdornment>
                      ),
                      sx: { borderRadius: 2 },
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Section>

        <Section title="Contact Details" icon={Email}>
          <Typography variant="subtitle2" color="text.secondary" mb={2}>
            Add at least one phone number (required)
          </Typography>

          {formValues.emails?.map((email, index) => (
            <Paper
              key={index}
              elevation={0}
              sx={{
                p: 2,
                mb: 2,
                bgcolor: "background.paper",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                position: "relative",
              }}
            >
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6}>
                  <Controller
                    name={`emails.${index}.email`}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Email"
                        fullWidth
                        variant="outlined"
                        size="small"
                        error={!!errors.emails?.[index]?.email}
                        helperText={
                          errors.emails?.[index]?.email?.message ||
                          (errors.emails?.[index]?.email?.type ===
                            "duplicate" &&
                            "Duplicate email")
                        }
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Email color="primary" />
                            </InputAdornment>
                          ),
                          sx: { borderRadius: 2 },
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={10} sm={4}>
                  <Controller
                    name={`emails.${index}.type`}
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth>
                        <InputLabel>Type *</InputLabel>
                        <Select
                          {...field}
                          label="Type *"
                          variant="outlined"
                          size="small"
                          error={!!errors.emails?.[index]?.type}
                          InputProps={{
                            sx: { borderRadius: 2 },
                          }}
                        >
                          {emailTypeOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>
                <Grid item xs={2} sm={2}>
                  <Box display="flex" justifyContent="flex-end">
                    {formValues.emails.length > 1 && (
                      <Tooltip title="Remove email">
                        <IconButton
                          onClick={() => removeArrayItem("emails", index)}
                          color="error"
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          ))}

          <Button
            variant="outlined"
            startIcon={<Add />}
            onClick={() =>
              addArrayItem("emails", {
                email: "",
                type: "work",
                primary: false,
              })
            }
            sx={{ mb: 4 }}
          >
            Add Email
          </Button>

          <Divider sx={{ my: 3 }} />

          {formValues.phones?.map((phone, index) => (
            <Paper
              key={index}
              elevation={0}
              sx={{
                p: 2,
                mb: 2,
                bgcolor: "background.paper",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                position: "relative",
              }}
            >
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={4}>
                  <Controller
                    name={`phones.${index}.number`}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Phone Number *"
                        fullWidth
                        size="small"
                        variant="outlined"
                        error={!!errors.phones?.[index]?.number}
                        helperText={
                          errors.phones?.[index]?.number?.message ||
                          (errors.phones?.[index]?.number?.type ===
                            "duplicate" &&
                            "Duplicate phone number")
                        }
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Phone color="primary" />
                            </InputAdornment>
                          ),
                          sx: { borderRadius: 2 },
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Controller
                    name={`phones.${index}.name`}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Reference Name"
                        fullWidth
                        size="small"
                        variant="outlined"
                        placeholder="e.g., Assistant"
                        InputProps={{
                          sx: { borderRadius: 2 },
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Controller
                    name={`phones.${index}.type`}
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth>
                        <InputLabel>Type *</InputLabel>
                        <Select
                          {...field}
                          label="Type *"
                          variant="outlined"
                          size="small"
                          error={!!errors.phones?.[index]?.type}
                        >
                          {phoneTypeOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>
                <Grid item xs={4} sm={2} md={1}>
                  <Box display="flex" justifyContent="flex-end">
                    {formValues.phones.length > 1 && (
                      <Tooltip title="Remove phone">
                        <IconButton
                          onClick={() => removeArrayItem("phones", index)}
                          color="error"
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          ))}

          <Button
            variant="outlined"
            startIcon={<Add />}
            onClick={() =>
              addArrayItem("phones", {
                number: "",
                type: "mobile",
                primary: false,
                name: "",
              })
            }
          >
            Add Phone
          </Button>
          {errors.root?.message && (
            <FormHelperText error sx={{ mt: 2 }}>
              {errors.root.message}
            </FormHelperText>
          )}
        </Section>

        <Section title="Lead Information" icon={Business}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Controller
                name="source"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Source *</InputLabel>
                    <Select
                      {...field}
                      label="Source *"
                      variant="outlined"
                      size="small"
                      error={!!errors.source}
                    >
                      {sourceOptions.map((option) => (
                        <MenuItem key={option.value} value={option.id}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.source && (
                      <FormHelperText error>
                        {errors.source.message}
                      </FormHelperText>
                    )}
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Status *</InputLabel>
                    <Select
                      {...field}
                      label="Status *"
                      variant="outlined"
                      size="small"
                      error={!!errors.status}
                    >
                      {statusOptions.map((option) => (
                        <MenuItem key={option.value} value={option.id}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.status && (
                      <FormHelperText error>
                        {errors.status.message}
                      </FormHelperText>
                    )}
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <CustomAutoCompleteWithLogo
                dataList={users?.map((user) => ({
                  ...user,
                  label: user.username, // Only add label if absolutely required
                  image: user.image_url,
                }))}
                selectedValue={empData}
                updateDataFunction={(e, newValue) => {
                  setEmpData(newValue);
                  setValue("assigned_to", newValue?.id || ""); // Store ID
                }}
                placeHolder="Select user name *"
                showAvathar={showAutoCompleteAvatar}
                getOptionLabel={(option) => option?.username} // Display username
                getOptionImage={(option) => option?.image_url}
                setShowAutoCompleteAvathar={setShowAutoCompleteAvatar}
                error={!!errors.assigned_to}
              />
              {errors.assigned_to && (
                <FormHelperText error sx={{ mt: -1, ml: 2 }}>
                  {errors.assigned_to.message}
                </FormHelperText>
              )}
            </Grid>
          </Grid>
        </Section>

        <Section title="Addresses" icon={LocationOn}>
          {formValues.addresses?.map((addr, index) => (
            <Paper
              key={index}
              elevation={0}
              sx={{
                p: 3,
                mb: 3,
                bgcolor: "background.paper",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                position: "relative",
              }}
            >
              {formValues.addresses.length > 1 && (
                <IconButton
                  onClick={() => removeArrayItem("addresses", index)}
                  color="error"
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    zIndex: 1,
                    backgroundColor: "background.paper",
                    "&:hover": {
                      backgroundColor: "action.hover",
                    },
                  }}
                >
                  <Delete />
                </IconButton>
              )}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name={`addresses.${index}.street`}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Street"
                        fullWidth
                        variant="outlined"
                        size="small"
                        InputProps={{
                          sx: { borderRadius: 2 },
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name={`addresses.${index}.city`}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="City"
                        fullWidth
                        variant="outlined"
                        size="small"
                        InputProps={{
                          sx: { borderRadius: 2 },
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name={`addresses.${index}.state`}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="State"
                        fullWidth
                        variant="outlined"
                        size="small"
                        InputProps={{
                          sx: { borderRadius: 2 },
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name={`addresses.${index}.zip`}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="ZIP"
                        fullWidth
                        variant="outlined"
                        size="small"
                        InputProps={{
                          sx: { borderRadius: 2 },
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name={`addresses.${index}.country`}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Country"
                        fullWidth
                        variant="outlined"
                        size="small"
                        InputProps={{
                          sx: { borderRadius: 2 },
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name={`addresses.${index}.type`}
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth>
                        <InputLabel>Type *</InputLabel>
                        <Select
                          {...field}
                          label="Type *"
                          variant="outlined"
                          size="small"
                          error={!!errors.addresses?.[index]?.type}
                        >
                          {addressTypeOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.addresses?.[index]?.type && (
                          <FormHelperText error>
                            {errors.addresses[index]?.type?.message}
                          </FormHelperText>
                        )}
                      </FormControl>
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name={`addresses.${index}.latitude`}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Latitude"
                        fullWidth
                        variant="outlined"
                        size="small"
                        type="number"
                        InputProps={{
                          sx: { borderRadius: 2 },
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name={`addresses.${index}.longitude`}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Longitude"
                        fullWidth
                        variant="outlined"
                        size="small"
                        type="number"
                        InputProps={{
                          sx: { borderRadius: 2 },
                        }}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Paper>
          ))}
          <Button
            variant="outlined"
            startIcon={<Add />}
            onClick={() =>
              addArrayItem("addresses", {
                street: "",
                city: "",
                state: "",
                zip: "",
                country: "",
                latitude: "",
                longitude: "",
                type: "work",
                primary: false,
              })
            }
          >
            Add Address
          </Button>
        </Section>

        <Section title="Social Media & Website" icon={Language}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Controller
                name="social_media.website"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Website"
                    fullWidth
                    variant="outlined"
                    size="small"
                    placeholder="https://example.com"
                    error={!!errors.social_media?.website}
                    helperText={errors.social_media?.website?.message}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Public color="primary" />
                        </InputAdornment>
                      ),
                      sx: { borderRadius: 2 },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="social_media.linkedin"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="LinkedIn Profile URL"
                    fullWidth
                    variant="outlined"
                    size="small"
                    placeholder="https://linkedin.com/in/..."
                    error={!!errors.social_media?.linkedin}
                    helperText={errors.social_media?.linkedin?.message}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LinkedIn color="primary" />
                        </InputAdornment>
                      ),
                      sx: { borderRadius: 2 },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="social_media.twitter"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Twitter Handle"
                    fullWidth
                    variant="outlined"
                    size="small"
                    placeholder="@username"
                    error={!!errors.social_media?.twitter}
                    helperText={errors.social_media?.twitter?.message}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Twitter color="primary" />
                        </InputAdornment>
                      ),
                      sx: { borderRadius: 2 },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="social_media.facebook"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Facebook Profile URL"
                    fullWidth
                    variant="outlined"
                    size="small"
                    placeholder="https://facebook.com/..."
                    error={!!errors.social_media?.facebook}
                    helperText={errors.social_media?.facebook?.message}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Facebook color="primary" />
                        </InputAdornment>
                      ),
                      sx: { borderRadius: 2 },
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Section>

        <Section title="Additional Details" icon={Note}>
          <Box mb={4}>
            <Controller
              name="notes"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Notes"
                  fullWidth
                  variant="outlined"
                  size="small"
                  multiline
                  rows={4}
                  InputProps={{
                    sx: { borderRadius: 2 },
                  }}
                />
              )}
            />
          </Box>
        </Section>

        <Box
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            bgcolor: "background.paper",
            borderTop: 1,
            borderColor: "divider",
            p: 2,
            zIndex: 1000,
            boxShadow: 3,
          }}
        >
          <Box
            display="flex"
            justifyContent="flex-end"
            maxWidth={2000}
            mx="auto"
          >
            <Box display="flex" gap={2}>
              <Button
                variant="outlined"
                onClick={() => history.push("/admin/crm/contacts")}
                //to="/admin/crm/contacts"
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                type="submit"
                disabled={isSaving}
                startIcon={
                  isSaving ? <CircularProgress size={20} /> : <Check />
                }
                sx={{
                  backgroundColor: "#84DEB8",
                  color: "#000",
                  "&:hover": { backgroundColor: "#6cc9a1" },
                }}
              >
                Update
              </Button>
            </Box>
          </Box>
        </Box>
      </form>
    </Box>
  );
}
